// ============================================================================
// GRAPH VISUALIZER  v2
//
// Pure SVG renderer for all graph algorithm payloads.
//
// Architecture
// ────────────
//   • Container-measured viewport via ResizeObserver — the SVG always fills
//     its parent div at device-pixel accuracy.
//   • Stable layout cache (stableLayoutRef) keyed on the sorted vertex-id set.
//     Layout is computed once per unique vertex set and reused across every
//     playback frame, so the graph does not jump during scrubbing.
//   • Separate rendering passes (edges first, nodes second, highlight layer
//     last) so SVG z-order is always: edges < node fills < node labels <
//     glow rings.  No sub-component recursion — all elements are flat SVG
//     primitives in a single <svg> tree.
//
// Edge rendering
// ──────────────
//   • Directed edges use per-state <marker> arrowheads defined in <defs>.
//     Four marker variants: default, active, mst, path.
//   • Bidirectional edges (A→B and B→A both present) are detected and offset
//     perpendicular to the edge axis using computeParallelOffset().
//   • Edge weight labels sit at a fixed perpendicular offset from the
//     trimmed-edge midpoint, never inside a node circle.
//   • Self-loop edges are rendered as a small quadratic arc above the node.
//
// Node rendering
// ──────────────
//   • Distance badges (Dijkstra / Bellman-Ford) appear above the node.
//   • BFS queue / DFS stack position badge appears below the node when
//     metadata contains queue or stack arrays.
//
// Focal highlight pass
// ────────────────────
//   • currentVertex  → amber pulsing halo ring + SVG feGaussianBlur glow
//   • currentEdge    → thickened amber edge drawn on top of all other edges
// ============================================================================

import React, { useMemo, useRef, useEffect, useState, memo } from 'react';
import type {
  ExecutionStep,
  GraphStatePayload,
  Vertex,
  Edge,
} from '../../../core/types/rust-engine-interfaces';
import { useAccessibility } from '../../../core/context/AccessibilityContext';
import {
  computeGraphLayout,
  trimEdgeEndpoints,
  computeParallelOffset,
  NODE_RADIUS,
  type LayoutPoint,
} from '../../../core/utils/graphLayout';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface GraphVisualizerProps {
  currentStep: ExecutionStep | null;
  darkMode?: boolean;
  algorithmId?: string;
}

type GraphSignature =
  | 'bfs' | 'dfs' | 'iddfs' | 'bidir-bfs' | 'dijkstra' | 'bellman-ford'
  | 'johnson' | 'astar' | 'kruskal' | 'prim' | 'boruvka'
  | 'ford-fulkerson' | 'edmonds-karp' | 'dinic' | 'max-flow' | 'min-cut'
  | 'kosaraju' | 'tarjan' | 'kahns' | 'dfs-topo' | 'topo-sort'
  | 'euler' | 'bridges' | 'hopcroft-karp' | 'min-cost-flow'
  | 'stoer-wagner' | 'boykov' | 'chu-liu' | 'gomory-hu'
  | 'centroid' | 'dominator' | 'dsu-rollback' | 'hld' | 'link-cut'
  | 'micali-vazirani' | 'rerooting' | 'bfs-dp' | 'blossom'
  | 'top-trees' | 'virtual-tree'
  | 'convex-hull' | 'closest-pair' | 'line-intersection' | 'point-in-polygon' | 'sat'
  | 'none';

function detectGraphSignature(algorithmId: string): GraphSignature {
  const id = algorithmId.toLowerCase();
  if (id.includes('bidirectional')) return 'bidir-bfs';
  if (id.includes('iterative-deepening')) return 'iddfs';
  if (id.includes('bfs') && id.includes('dp')) return 'bfs-dp';
  if (id.includes('bfs') && !id.includes('hybrid')) return 'bfs';
  if (id.includes('dfs-topological')) return 'dfs-topo';
  if (id.includes('dfs')) return 'dfs';
  if (id.includes('bellman')) return 'bellman-ford';
  if (id.includes('johnson')) return 'johnson';
  if (id.includes('astar') || id.includes('a-star')) return 'astar';
  if (id.includes('dijkstra')) return 'dijkstra';
  if (id.includes('kruskal')) return 'kruskal';
  if (id.includes('prim') && !id.includes('boruvka')) return 'prim';
  if (id.includes('boruvka')) return 'boruvka';
  if (id.includes('edmonds-karp')) return 'edmonds-karp';
  if (id.includes('dinic')) return 'dinic';
  if (id.includes('ford-fulkerson')) return 'ford-fulkerson';
  if (id.includes('max-flow')) return 'max-flow';
  if (id.includes('min-cut') && !id.includes('stoer')) return 'min-cut';
  if (id.includes('stoer')) return 'stoer-wagner';
  if (id.includes('min-cost')) return 'min-cost-flow';
  if (id.includes('boykov')) return 'boykov';
  if (id.includes('kosaraju')) return 'kosaraju';
  if (id.includes('tarjan')) return 'tarjan';
  if (id.includes('kahns') || id.includes('kahn')) return 'kahns';
  if (id.includes('topological-sort') || id.includes('topo-sort')) return 'topo-sort';
  if (id.includes('euler')) return 'euler';
  if (id.includes('bridges') || id.includes('articulation')) return 'bridges';
  if (id.includes('hopcroft')) return 'hopcroft-karp';
  if (id.includes('chu-liu')) return 'chu-liu';
  if (id.includes('gomory')) return 'gomory-hu';
  if (id.includes('centroid')) return 'centroid';
  if (id.includes('dominator')) return 'dominator';
  if (id.includes('dsu-rollback') || id.includes('rollback')) return 'dsu-rollback';
  if (id.includes('heavy-light') || id.includes('hld')) return 'hld';
  if (id.includes('link-cut')) return 'link-cut';
  if (id.includes('micali') || id.includes('vazirani')) return 'micali-vazirani';
  if (id.includes('rerooting') || id.includes('re-rooting')) return 'rerooting';
  if (id.includes('blossom')) return 'blossom';
  if (id.includes('top-tree')) return 'top-trees';
  if (id.includes('virtual-tree') || id.includes('virtualtree')) return 'virtual-tree';
  if (id.includes('convex-hull')) return 'convex-hull';
  if (id.includes('closest-pair')) return 'closest-pair';
  if (id.includes('line-intersection') || id.includes('line-segment')) return 'line-intersection';
  if (id.includes('point-in-polygon')) return 'point-in-polygon';
  if (id.includes('2-sat') || id.includes('sat')) return 'sat';
  return 'none';
}

// ── Color tokens ──────────────────────────────────────────────────────────────

const C = {
  // Node fills
  nodeFillDefault:   'rgba(10, 18, 32, 0.95)',
  nodeFillVisited:   'rgba(3, 24, 16, 0.92)',
  nodeFillActive:    'rgba(42, 30, 0, 0.95)',
  nodeFillPath:      'rgba(20, 10, 50, 0.92)',
  nodeFillMst:       'rgba(3, 30, 30, 0.92)',
  nodeFillStart:     'rgba(8, 18, 45, 0.92)',

  // Node strokes
  strokeDefault: '#1e3348',
  strokeVisited: '#22c55e',
  strokeActive:  '#f59e0b',
  strokePath:    '#818cf8',
  strokeMst:     '#22d3ee',
  strokeStart:   '#60a5fa',

  // Edge strokes
  edgeDefault:  '#334155',
  edgeVisited:  '#22c55e',
  edgeActive:   '#f59e0b',
  edgePath:     '#818cf8',
  edgeMst:      '#22d3ee',
  edgeFlow:     '#a855f7',
  edgeResidual: '#64748b',

  // Text
  labelDefault: '#7d95b0',
  labelVisited: '#4ade80',
  labelActive:  '#fde68a',
  labelPath:    '#c7d2fe',
  labelMst:     '#5eead4',
  labelStart:   '#93c5fd',

  // Misc
  glow:         '#f59e0b',
  distBadgeBg:  'rgba(6,6,10,0.92)',
  distBadgeFg:  '#60a5fa',
};

// Arrow head marker sizes
const ARROW_W = 12;
const ARROW_H = 12;
const ARROW_CLEARANCE = 6; // arrowClearance so tip lands on node boundary, not inside

// ── Node state resolution ─────────────────────────────────────────────────────

type NodeState = 'default' | 'visited' | 'active' | 'path' | 'mst' | 'start' | 'end';

function resolveNodeState(
  vertexId: string,
  vertex: Vertex,
  meta: GraphStatePayload['metadata'],
): NodeState {
  if (meta.currentVertex === vertexId) return 'active';
  if (meta.startVertex === vertexId) return 'start';
  if (meta.endVertex === vertexId) return 'end';
  if (meta.path?.includes(vertexId)) return 'path';
  if (meta.visitedVertices?.includes(vertexId)) return 'visited';
  if (vertex.color === 'visited') return 'visited';
  if (vertex.color === 'exploring') return 'active';
  return 'default';
}

function nodeColors(state: NodeState): { fill: string; stroke: string; sw: number; label: string; glow: boolean } {
  switch (state) {
    case 'active':   return { fill: C.nodeFillActive,  stroke: C.strokeActive,  sw: 2.5, label: C.labelActive,  glow: true  };
    case 'visited':  return { fill: C.nodeFillVisited, stroke: C.strokeVisited, sw: 2,   label: C.labelVisited, glow: false };
    case 'path':     return { fill: C.nodeFillPath,    stroke: C.strokePath,    sw: 2.5, label: C.labelPath,    glow: false };
    case 'mst':      return { fill: C.nodeFillMst,     stroke: C.strokeMst,     sw: 2,   label: C.labelMst,     glow: false };
    case 'start':    return { fill: C.nodeFillStart,   stroke: C.strokeStart,   sw: 2,   label: C.labelStart,   glow: false };
    case 'end':      return { fill: 'rgba(45, 8, 18, 0.92)', stroke: '#f43f5e', sw: 2,   label: '#fda4af',      glow: false };
    default:         return { fill: C.nodeFillDefault, stroke: C.strokeDefault, sw: 1.5, label: C.labelDefault, glow: false };
  }
}

// ── Edge state resolution ─────────────────────────────────────────────────────

type EdgeState = 'default' | 'active' | 'visited' | 'mst' | 'path' | 'flow' | 'residual';

function resolveEdgeState(
  edge: Edge,
  meta: GraphStatePayload['metadata'],
): EdgeState {
  const key = `${edge.from}-${edge.to}`;
  const revKey = `${edge.to}-${edge.from}`;
  const cur = meta.currentEdge;
  if (cur) {
    const curKey = `${cur.from}-${cur.to}`;
    if (edge.id && edge.id === cur.id) return 'active';
    if (curKey === key || curKey === revKey) return 'active';
  }
  if (edge.residual) return 'residual';
  if (edge.flow !== undefined && edge.capacity !== undefined) return 'flow';
  if (meta.mstEdges) {
    const inMst = meta.mstEdges.some(me =>
      (me.from === edge.from && me.to === edge.to) ||
      (me.from === edge.to && me.to === edge.from)
    );
    if (inMst) return 'mst';
  }
  if (meta.path && meta.path.length > 1) {
    for (let i = 0; i < meta.path.length - 1; i++) {
      const a = meta.path[i], b = meta.path[i + 1];
      if ((a === edge.from && b === edge.to) || (a === edge.to && b === edge.from)) return 'path';
    }
  }
  if (meta.traversalTreeEdges) {
    const inTree = meta.traversalTreeEdges.some(te =>
      (te.from === edge.from && te.to === edge.to) ||
      (te.from === edge.to && te.to === edge.from)
    );
    if (inTree) return 'visited';
  }
  if (edge.color === 'exploring') return 'active';
  if (edge.color === 'visited')   return 'visited';
  if (edge.color === 'mst')       return 'mst';
  if (edge.color === 'path')      return 'path';
  return 'default';
}

function edgeStyle(state: EdgeState, isResidual: boolean = false): { stroke: string; sw: number; opacity: number; markerSuffix: string; strokeDasharray?: string } {
  const baseStyle: Record<EdgeState, { stroke: string; sw: number; opacity: number; markerSuffix: string }> = {
    active:   { stroke: C.edgeActive,   sw: 3,   opacity: 1,    markerSuffix: 'active'   },
    visited:  { stroke: C.edgeVisited,  sw: 2,   opacity: 0.85, markerSuffix: 'visited'  },
    mst:      { stroke: C.edgeMst,      sw: 2.5, opacity: 1,    markerSuffix: 'mst'      },
    path:     { stroke: C.edgePath,     sw: 3,   opacity: 1,    markerSuffix: 'path'     },
    flow:     { stroke: C.edgeFlow,     sw: 2.5, opacity: 1,    markerSuffix: 'flow'     },
    residual: { stroke: C.edgeResidual, sw: 1.5, opacity: 0.4,  markerSuffix: 'residual' },
    default:  { stroke: C.edgeDefault,  sw: 1.5, opacity: 0.45, markerSuffix: 'default'  },
  };

  const style = baseStyle[state] || baseStyle.default;

  if (isResidual) {
    return { ...style, strokeDasharray: '4,3', opacity: style.opacity * 0.7 };
  }

  return style;
}

// ── Stable layout cache key ───────────────────────────────────────────────────

function layoutKey(vertices: Vertex[]): string {
  return vertices
    .map((v) => v.id)
    .sort()
    .join('|');
}

// ── Component ──────────────────────────────────────────────────────────────────

// ── Color-blind shape overlays ───────────────────────────────────────────────
// Each node state maps to a distinct SVG shape drawn inside the node circle
// so state is distinguishable without color.

function CbShapeOverlay({ cx, cy, r, state }: { cx: number; cy: number; r: number; state: string }) {
  const s = r * 0.45;
  switch (state) {
    case 'active':   // diamond outline
      return <polygon points={`${cx},${cy-s} ${cx+s},${cy} ${cx},${cy+s} ${cx-s},${cy}`} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    case 'visited':  // circle dot outline
      return <circle cx={cx} cy={cy} r={s * 0.65} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    case 'path':     // triangle outline
      return <polygon points={`${cx},${cy-s} ${cx+s},${cy+s*0.6} ${cx-s},${cy+s*0.6}`} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    case 'mst':      // plus / cross
      return (
        <g aria-hidden="true" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} fill="none">
          <line x1={cx} y1={cy-s} x2={cx} y2={cy+s} />
          <line x1={cx-s} y1={cy} x2={cx+s} y2={cy} />
        </g>
      );
    case 'start':    // hollow square
      return <rect x={cx-s*0.6} y={cy-s*0.6} width={s*1.2} height={s*1.2} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth={1.5} aria-hidden="true" />;
    default:
      return null;
  }
}

export const GraphVisualizer: React.FC<GraphVisualizerProps> = memo(({
  currentStep,
  darkMode: _darkMode = true,
  algorithmId = '',
}) => {
  const { colorBlindMode } = useAccessibility();
  const containerRef  = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 600, h: 400 });

  // Stable layout: keyed on sorted vertex-id set, shared across frames
  const stableLayoutRef     = useRef<Map<string, LayoutPoint>>(new Map());
  const stableLayoutKeyRef  = useRef<string>('');
  // Viewport dimensions used when the layout was last computed
  const layoutSizeRef       = useRef<{ w: number; h: number }>({ w: 600, h: 400 });

  // ── Container measurement ─────────────────────────────────────────────────

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ w: Math.max(200, Math.round(width)), h: Math.max(150, Math.round(height)) });
    });
    ro.observe(el);
    // Seed with current dimensions
    const rect = el.getBoundingClientRect();
    setSize({ w: Math.max(200, Math.round(rect.width)), h: Math.max(150, Math.round(rect.height)) });
    return () => ro.disconnect();
  }, []);

  const { w: W, h: H } = size;

  // ── Payload extraction ────────────────────────────────────────────────────

  const graphState = useMemo((): GraphStatePayload | null => {
    if (!currentStep || currentStep.payload.type !== 'graph') return null;
    return currentStep.payload as GraphStatePayload;
  }, [currentStep]);

  // ── Stable layout computation ─────────────────────────────────────────────
  //
  // Only recalculate when the set of vertices changes.  During playback the
  // vertex set is constant, so positions are stable across all frames.

  // posMap depends only on the vertex set (graphState), NOT on W/H.
  // When the vertex set changes we run the full layout at the current viewport.
  // When only W/H change we proportionally rescale the stored positions.
  const posMap = useMemo<Map<string, LayoutPoint>>(() => {
    const vertices = graphState?.vertices ?? [];
    const edges    = graphState?.edges    ?? [];
    if (vertices.length === 0) return new Map();

    const key = layoutKey(vertices);
    if (key === stableLayoutKeyRef.current && stableLayoutRef.current.size > 0) {
      return stableLayoutRef.current;
    }

    try {
      const cached = sessionStorage.getItem(`algo-viz-layout-${key}`);
      if (cached) {
        const parsed = JSON.parse(cached) as Record<string, LayoutPoint>;
        const layout = new Map<string, LayoutPoint>();
        Object.entries(parsed).forEach(([id, pt]) => layout.set(id, pt));
        if (layout.size === vertices.length) {
          stableLayoutRef.current    = layout;
          stableLayoutKeyRef.current = key;
          layoutSizeRef.current      = { w: W, h: H };
          return layout;
        }
      }
    } catch {
      // ignore corrupt cache
    }

    const layout = computeGraphLayout(vertices, edges, { width: W, height: H, padding: 56 }, algorithmId);
    stableLayoutRef.current    = layout;
    stableLayoutKeyRef.current = key;
    layoutSizeRef.current      = { w: W, h: H };
    try {
      const serial: Record<string, LayoutPoint> = {};
      layout.forEach((pt, id) => { serial[id] = pt; });
      sessionStorage.setItem(`algo-viz-layout-${key}`, JSON.stringify(serial));
    } catch {
      // ignore quota errors
    }
    return layout;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphState]);

  // When the viewport resizes but the vertex set has NOT changed, rescale
  // existing positions proportionally rather than re-running the layout engine.
  const scaledPosMap = useMemo<Map<string, LayoutPoint>>(() => {
    if (posMap.size === 0) return posMap;
    const { w: lw, h: lh } = layoutSizeRef.current;
    if (lw === W && lh === H) return posMap;
    const scaleX = W / Math.max(lw, 1);
    const scaleY = H / Math.max(lh, 1);
    const scaled = new Map<string, LayoutPoint>();
    posMap.forEach((pt, id) => {
      scaled.set(id, { id: pt.id, x: pt.x * scaleX, y: pt.y * scaleY });
    });
    return scaled;
  }, [posMap, W, H]);

  const getPos = (id: string): LayoutPoint | null => scaledPosMap.get(id) ?? null;

  // ── Bidirectional edge detection ──────────────────────────────────────────

  const edgePairSet = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    const edges = graphState?.edges ?? [];
    edges.forEach((e) => {
      if (edges.some((e2) => e2.from === e.to && e2.to === e.from)) {
        s.add(`${e.from}|${e.to}`);
      }
    });
    return s;
  }, [graphState]);

  // ── Distance / queue / stack data ─────────────────────────────────────────

  const distances = useMemo<Map<string, number>>(() => {
    const m   = new Map<string, number>();
    const raw = graphState?.metadata.distances;
    if (!raw) return m;
    Object.entries(raw).forEach(([id, d]) => {
      if (Number.isFinite(d)) m.set(id, d);
    });
    return m;
  }, [graphState]);

  // ── Visit order map (prefer traversalOrder, fall back to visitedVertices) ─
  const visitOrder = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>();
    const meta = graphState?.metadata;
    if (!meta) return m;
    const travOrder = meta.traversalOrder;
    if (travOrder && Object.keys(travOrder).length > 0) {
      Object.entries(travOrder).forEach(([id, n]) => m.set(id, n));
    } else {
      const visited = meta.visitedVertices ?? [];
      visited.forEach((id, i) => m.set(id, i + 1));
    }
    return m;
  }, [graphState]);

  // ── SCC component color map ───────────────────────────────────────────────
  const sccColors = useMemo<Map<string, string>>(() => {
    const m = new Map<string, string>();
    const meta = graphState?.metadata;
    const comps = meta?.components;
    if (!comps || comps.length === 0) return m;
    const palette = ['#ef4444', '#3b82f6', '#22c55e', '#fbbf24', '#a855f7', '#ec4899', '#06b6d4', '#f97316'];
    comps.forEach((comp, ci) => {
      const color = palette[ci % palette.length]!;
      comp.forEach((id) => m.set(id, color));
    });
    return m;
  }, [graphState]);

  // ── Empty state ───────────────────────────────────────────────────────────

  if (!graphState || graphState.vertices.length === 0) {
    return (
      <div ref={containerRef} className="flex items-center justify-center w-full h-full text-slate-500">
        <div className="text-center space-y-1">
          <p className="text-sm font-medium">No graph data</p>
          <p className="text-xs">Execute a graph algorithm to begin</p>
        </div>
      </div>
    );
  }

  const directed = graphState.metadata.directed ?? false;
  const weighted = graphState.metadata.weighted ?? false;
  const meta     = graphState.metadata;

  // ── SVG defs — arrowhead markers ──────────────────────────────────────────
  //
  // One marker per edge state so we never recolour with `currentColor` hacks.

  // Solid filled-polygon arrowheads: refX=11 places tip at line endpoint.
  // viewBox="0 0 12 12", polygon "0,1 12,6 0,11" gives a sharp symmetric triangle.
  const markerDefs = (
    <defs>
      <filter id="gv-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="5" result="blur" />
        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>

      {directed && (['default', 'active', 'visited', 'mst', 'path', 'flow', 'residual'] as EdgeState[]).map((s) => {
        const st = edgeStyle(s);
        return (
          <marker
            key={`m-${s}`}
            id={`arrow-${s}`}
            viewBox="0 0 12 12"
            refX="11"
            refY="6"
            markerWidth={ARROW_W / 2}
            markerHeight={ARROW_H / 2}
            orient="auto-start-reverse"
          >
            <polygon points="0,1 12,6 0,11" fill={st.stroke} />
          </marker>
        );
      })}
    </defs>
  );

  // ── Edge rendering pass ───────────────────────────────────────────────────

  const edgeElements = graphState.edges.map((edge: Edge) => {
    const fromPos = getPos(edge.from);
    const toPos   = getPos(edge.to);
    if (!fromPos || !toPos) return null;

    // Self-loop: larger arc above the node so it doesn't overlap the label
    if (edge.from === edge.to) {
      const cx = fromPos.x;
      const cy = fromPos.y - NODE_RADIUS * 1.6;
      const rx = NODE_RADIUS * 1.1;
      const ry = NODE_RADIUS * 0.9;
      const st = edgeStyle(resolveEdgeState(edge, meta));
      return (
        <g key={`edge-self-${edge.id ?? edge.from}`}>
          <path
            d={`M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx + rx} ${cy}`}
            fill="none"
            stroke={st.stroke}
            strokeWidth={st.sw}
            opacity={st.opacity}
          />
          {edge.weight !== undefined && (
            <text
              x={cx} y={cy - ry - 6}
              fill={st.stroke}
              fontSize="10"
              textAnchor="middle"
              dominantBaseline="middle"
              className="select-none"
            >
              {edge.weight}
            </text>
          )}
        </g>
      );
    }

    const state = resolveEdgeState(edge, meta);
    const st    = edgeStyle(state, edge.residual);
    const isBi  = edgePairSet.has(`${edge.from}|${edge.to}`);

    const clearance = directed ? ARROW_CLEARANCE : 0;
    const trimmed   = trimEdgeEndpoints(fromPos, toPos, NODE_RADIUS, clearance);

    // Perpendicular offset for bidirectional pairs
    let ox = 0;
    let oy = 0;
    if (isBi) {
      const off = computeParallelOffset(fromPos, toPos, 10, true);
      ox = off.ox;
      oy = off.oy;
    }

    const x1 = trimmed.x1 + ox;
    const y1 = trimmed.y1 + oy;
    const x2 = trimmed.x2 + ox;
    const y2 = trimmed.y2 + oy;

    // Weight label perpendicular to edge midpoint
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.max(Math.hypot(dx, dy), 0.001);
    const lx = mx + (-dy / len) * 14;
    const ly = my + (dx  / len) * 14;

    // Flow-specific styling
    const isFlowEdge = edge.flow !== undefined || edge.capacity !== undefined;
    const flowLabel = isFlowEdge && edge.flow !== undefined && edge.capacity !== undefined 
      ? `${edge.flow}/${edge.capacity}` 
      : isFlowEdge && edge.flow !== undefined 
      ? `flow: ${edge.flow}`
      : null;

    return (
      <g key={`edge-${edge.id ?? `${edge.from}-${edge.to}`}`}>
        <line
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={st.stroke}
          strokeWidth={st.sw}
          opacity={st.opacity}
          strokeDasharray={st.strokeDasharray}
          markerEnd={directed ? `url(#arrow-${state})` : undefined}
        />
        {weighted && edge.weight !== undefined && (
          <text
            x={lx} y={ly - 10}
            fill={state === 'active' ? C.labelActive : '#64748b'}
            fontSize="10"
            fontWeight={state === 'active' ? 'bold' : 'normal'}
            textAnchor="middle"
            dominantBaseline="middle"
            className="select-none"
          >
            {edge.weight}
          </text>
        )}
        {edge.flow !== undefined && edge.capacity !== undefined && (
          <text
            x={lx} y={ly - 10}
            fill={state === 'flow' ? '#c4b5fd' : '#64748b'}
            fontSize="10"
            fontWeight={state === 'flow' ? 'bold' : 'normal'}
            textAnchor="middle"
            dominantBaseline="middle"
            className="select-none"
          >
            {edge.flow}/{edge.capacity}
          </text>
        )}
        {edge.label && (
          <text
            x={lx} y={ly + (weighted ? 4 : 0)}
            fill={state === 'active' ? C.labelActive : '#94a3b8'}
            fontSize="9"
            fontWeight={state === 'active' ? 'bold' : 'normal'}
            textAnchor="middle"
            dominantBaseline="middle"
            className="select-none"
          >
            {edge.label}
          </text>
        )}
        {/* Flow label for max-flow algorithms */}
        {flowLabel && (
          <text
            x={lx} y={ly + (weighted ? (edge.label ? 8 : 4) : (edge.label ? 4 : 0))}
            fill={edge.residual ? '#ef4444' : (state === 'active' ? C.labelActive : '#10b981')}
            fontSize={edge.residual ? '8' : '9'}
            fontWeight={state === 'active' ? 'bold' : (edge.residual ? 'normal' : '600')}
            textAnchor="middle"
            dominantBaseline="middle"
            className="select-none"
            fontStyle={edge.residual ? 'italic' : 'normal'}
          >
            {flowLabel}
          </text>
        )}
      </g>
    );
  });

  // ── Traversal tree edge overlay ───────────────────────────────────────────
  const traversalTreeElements = useMemo(() => {
    const treeEdges = meta.traversalTreeEdges;
    if (!treeEdges || treeEdges.length === 0) return [];
    return treeEdges.map((te, i) => {
      const fp = getPos(te.from);
      const tp = getPos(te.to);
      if (!fp || !tp) return null;
      const t = trimEdgeEndpoints(fp, tp, NODE_RADIUS, 0);
      return (
        <line
          key={`tt-${i}`}
          x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke="#22d3ee"
          strokeWidth={3}
          opacity={0.7}
          strokeLinecap="round"
        />
      );
    }).filter(Boolean);
  }, [graphState]);

  // ── Node rendering pass ───────────────────────────────────────────────────

  const nodeElements = graphState.vertices.map((vertex: Vertex) => {
    const pos = getPos(vertex.id);
    if (!pos) return null;

    const state  = resolveNodeState(vertex.id, vertex, meta);
    const col    = nodeColors(state);
    const dist   = distances.get(vertex.id);
    const label  = String(vertex.label || vertex.id);

    // Queue / stack / priority queue position badge
    const queuePos = meta.queue?.indexOf(vertex.id);
    const stackPos = meta.stack?.indexOf(vertex.id);
    const pqPos    = meta.priorityQueue?.indexOf(vertex.id);
    const seqPos   = queuePos !== undefined && queuePos >= 0
      ? queuePos
      : stackPos !== undefined && stackPos >= 0
      ? stackPos
      : pqPos !== undefined && pqPos >= 0
      ? pqPos
      : null;

    return (
      <g
        key={`node-${vertex.id}`}
        role="img"
        aria-label={`Node ${label}, state: ${state}`}
      >
        {/* Amber glow halo for active node — filter lives here so it extends outward */}
        {col.glow && (
          <circle
            cx={pos.x} cy={pos.y} r={NODE_RADIUS + 8}
            fill={`${C.glow}1a`}
            stroke={C.glow}
            strokeWidth={1}
            opacity={0.55}
            filter="url(#gv-glow)"
            aria-hidden="true"
          />
        )}

        {/* Node circle — crisp fill, no blur */}
        <circle
          cx={pos.x}
          cy={pos.y}
          r={NODE_RADIUS}
          fill={col.fill}
          stroke={colorBlindMode ? `var(--cb-${state === 'default' ? 'default' : state})` : col.stroke}
          strokeWidth={state === 'active' ? 3 : col.sw}
          aria-hidden="true"
        />

        {/* Color-blind shape overlay — drawn inside the node */}
        {colorBlindMode && (
          <CbShapeOverlay cx={pos.x} cy={pos.y} r={NODE_RADIUS} state={state} />
        )}

        {/* Node label — capped at 4 chars to stay inside circle */}
        <text
          x={pos.x}
          y={pos.y}
          fill={col.label}
          fontSize={label.length > 2 ? '11' : '13'}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontFamily: 'monospace', userSelect: 'none', pointerEvents: 'none' }}
          aria-hidden="true"
        >
          {label.slice(0, 4)}
        </text>

        {/* Distance badge above node */}
        {dist !== undefined && (
          <g>
            <rect
              x={pos.x - 18}
              y={pos.y - NODE_RADIUS - 22}
              width={36}
              height={14}
              rx={4}
              fill={C.distBadgeBg}
              stroke="#1e3a5f"
              strokeWidth={0.5}
            />
            <text
              x={pos.x}
              y={pos.y - NODE_RADIUS - 15}
              fill={C.distBadgeFg}
              fontSize="9"
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="middle"
              className="select-none"
            >
              {dist === Infinity ? '∞' : dist}
            </text>
          </g>
        )}

        {/* Queue/stack position badge below node */}
        {seqPos !== null && (
          <text
            x={pos.x}
            y={pos.y + NODE_RADIUS + 12}
            fill="#475569"
            fontSize="9"
            textAnchor="middle"
            dominantBaseline="middle"
            className="select-none"
          >
            #{seqPos}
          </text>
        )}

        {/* SCC component colored ring — drawn behind node circle */}
        {sccColors.has(vertex.id) && (
          <circle
            cx={pos.x}
            cy={pos.y}
            r={NODE_RADIUS + 4}
            fill="none"
            stroke={sccColors.get(vertex.id)}
            strokeWidth={2}
            opacity={0.7}
            strokeDasharray="3,2"
          />
        )}

        {/* Visit order badge — small numbered circle for visited nodes */}
        {visitOrder.has(vertex.id) && (
          <g>
            <circle
              cx={pos.x - NODE_RADIUS + 6}
              cy={pos.y - NODE_RADIUS + 6}
              r={8}
              fill={state === 'active' ? C.strokeActive : C.strokeVisited}
              opacity={0.9}
            />
            <text
              x={pos.x - NODE_RADIUS + 6}
              y={pos.y - NODE_RADIUS + 6}
              fill="#0a0e14"
              fontSize="8"
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="middle"
              className="select-none"
            >
              {visitOrder.get(vertex.id)}
            </text>
          </g>
        )}
      </g>
    );
  });

  // ── Focal highlight pass ──────────────────────────────────────────────────
  //
  // Drawn last so it sits on top of all node fills and labels.

  const highlightElements: React.ReactElement[] = [];

  if (meta.currentVertex) {
    const pos = getPos(meta.currentVertex);
    if (pos) {
      highlightElements.push(
        <circle
          key="hl-node"
          cx={pos.x}
          cy={pos.y}
          r={NODE_RADIUS + 7}
          fill="none"
          stroke={C.glow}
          strokeWidth={2.5}
          opacity={0.85}
          filter="url(#gv-glow)"
        />,
      );
    }
  }

  if (meta.currentEdge) {
    const fp = getPos(meta.currentEdge.from);
    const tp = getPos(meta.currentEdge.to);
    if (fp && tp) {
      const t = trimEdgeEndpoints(fp, tp, NODE_RADIUS, directed ? ARROW_CLEARANCE : 0);
      highlightElements.push(
        <line
          key="hl-edge"
          x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={C.glow}
          strokeWidth={5}
          opacity={0.45}
          filter="url(#gv-glow)"
          markerEnd={directed ? 'url(#arrow-active)' : undefined}
        />,
      );
    }
  }

  // ── Algorithm-specific signature overlay ──────────────────────────────────
  const sigElements: React.ReactElement[] = [];
  const graphSig = detectGraphSignature(algorithmId ?? '');
  const expl = currentStep?.metadata?.explanation ?? '';

  if (graphSig !== 'none' && graphState) {
    const cv = meta.currentVertex;
    const cvPos = cv ? getPos(cv) : null;

    switch (graphSig) {
      case 'bfs':
        // Concentric level ring around current vertex
        if (cvPos) {
          sigElements.push(
            <circle key="sig-bfs" cx={cvPos.x} cy={cvPos.y} r={NODE_RADIUS + 12}
              fill="none" stroke="#3b82f6" strokeWidth={1} strokeDasharray="3 3" opacity={0.5} />,
            <text key="sig-bfs-lbl" x={cvPos.x + NODE_RADIUS + 14} y={cvPos.y - NODE_RADIUS - 8}
              fill="#3b82f6" fontSize="8" fontWeight="bold">level</text>,
          );
        }
        break;

      case 'dfs':
        // Depth spiral around current vertex
        if (cvPos) {
          sigElements.push(
            <path key="sig-dfs" d={`M ${cvPos.x + NODE_RADIUS + 4} ${cvPos.y} a 8 8 0 1 1 -0.1 0`}
              fill="none" stroke="#f472b6" strokeWidth={1.5} opacity={0.7} />,
            <text key="sig-dfs-lbl" x={cvPos.x + NODE_RADIUS + 16} y={cvPos.y + 4}
              fill="#f472b6" fontSize="8" fontWeight="bold">depth</text>,
          );
        }
        break;

      case 'iddfs':
        // Depth limit indicator
        if (cvPos) {
          const depthMatch = expl.match(/depth\s*(?:limit|=)\s*(\d+)/i);
          const depth = depthMatch ? depthMatch[1] : '?';
          sigElements.push(
            <text key="sig-iddfs" x={cvPos.x} y={cvPos.y - NODE_RADIUS - 28}
              fill="#f97316" fontSize="9" fontWeight="bold" textAnchor="middle">depth≤{depth}</text>,
          );
        }
        break;

      case 'bidir-bfs':
        // Two-sided meeting indicator
        if (cvPos) {
          sigElements.push(
            <circle key="sig-bidir" cx={cvPos.x} cy={cvPos.y} r={NODE_RADIUS + 10}
              fill="none" stroke="#22d3ee" strokeWidth={1.5} strokeDasharray="2 4" opacity={0.6} />,
            <text key="sig-bidir-lbl" x={cvPos.x + NODE_RADIUS + 14} y={cvPos.y - 4}
              fill="#22d3ee" fontSize="8" fontWeight="bold">meet</text>,
          );
        }
        break;

      case 'dijkstra':
        // "Select min" badge
        if (cvPos && expl.toLowerCase().includes('select')) {
          sigElements.push(
            <text key="sig-dijkstra" x={cvPos.x} y={cvPos.y - NODE_RADIUS - 28}
              fill="#fbbf24" fontSize="9" fontWeight="bold" textAnchor="middle">min dist</text>,
          );
        }
        // Relaxation arrow on current edge
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp && expl.toLowerCase().includes('relax')) {
            sigElements.push(
              <text key="sig-dijkstra-relax"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#22c55e" fontSize="8" fontWeight="bold" textAnchor="middle">relax</text>,
            );
          }
        }
        break;

      case 'bellman-ford':
        // Edge iteration counter
        sigElements.push(
          <text key="sig-bf" x={W - 12} y={20}
            fill="#f97316" fontSize="9" fontWeight="bold" textAnchor="end">edge iter</text>,
        );
        break;

      case 'johnson':
        // Johnson badge: reweight + Bellman-Ford + Dijkstra
        sigElements.push(
          <text key="sig-johnson" x={W - 12} y={20}
            fill="#a78bfa" fontSize="9" fontWeight="bold" textAnchor="end">J:BF+Dij</text>,
        );
        break;

      case 'astar':
        // A* f-score badge
        if (cvPos) {
          sigElements.push(
            <text key="sig-astar" x={cvPos.x} y={cvPos.y - NODE_RADIUS - 28}
              fill="#06b6d4" fontSize="9" fontWeight="bold" textAnchor="middle">f=g+h</text>,
          );
        }
        break;

      case 'kruskal':
        // Cycle-reject cross on skipped edges
        if (meta.currentEdge && expl.toLowerCase().includes('skip')) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            const mx = (fp.x + tp.x) / 2;
            const my = (fp.y + tp.y) / 2;
            sigElements.push(
              <g key="sig-kruskal-reject">
                <line x1={mx - 8} y1={my - 8} x2={mx + 8} y2={my + 8} stroke="#ef4444" strokeWidth={2} />
                <line x1={mx + 8} y1={my - 8} x2={mx - 8} y2={my + 8} stroke="#ef4444" strokeWidth={2} />
              </g>,
            );
          }
        }
        // "Add" label on accepted edges
        if (meta.currentEdge && expl.toLowerCase().includes('add')) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-kruskal-add"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#22c55e" fontSize="8" fontWeight="bold" textAnchor="middle">union</text>,
            );
          }
        }
        break;

      case 'prim':
        // Crossing-edge indicator
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-prim"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#14b8a6" fontSize="8" fontWeight="bold" textAnchor="middle">cross</text>,
            );
          }
        }
        break;

      case 'boruvka':
        // Boruvka: component-min edge
        sigElements.push(
          <text key="sig-boruvka" x={W - 12} y={20}
            fill="#facc15" fontSize="9" fontWeight="bold" textAnchor="end">comp-min</text>,
        );
        break;

      case 'ford-fulkerson':
        // Augmenting path indicator (DFS-based)
        if (expl.toLowerCase().includes('augment') || expl.toLowerCase().includes('path')) {
          sigElements.push(
            <text key="sig-flow" x={W - 12} y={20}
              fill="#3b82f6" fontSize="9" fontWeight="bold" textAnchor="end">DFS path</text>,
          );
        }
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-flow-bottleneck"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#60a5fa" fontSize="8" fontWeight="bold" textAnchor="middle">bottleneck</text>,
            );
          }
        }
        break;

      case 'edmonds-karp':
        // Augmenting path indicator (BFS shortest)
        if (expl.toLowerCase().includes('augment') || expl.toLowerCase().includes('path')) {
          sigElements.push(
            <text key="sig-flow" x={W - 12} y={20}
              fill="#06b6d4" fontSize="9" fontWeight="bold" textAnchor="end">BFS shortest</text>,
          );
        }
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-flow-bottleneck"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#22d3ee" fontSize="8" fontWeight="bold" textAnchor="middle">bottleneck</text>,
            );
          }
        }
        break;

      case 'dinic':
        // Level graph + blocking flow
        sigElements.push(
          <text key="sig-dinic-level" x={W - 12} y={20}
            fill="#a78bfa" fontSize="9" fontWeight="bold" textAnchor="end">level graph</text>,
        );
        if (expl.toLowerCase().includes('augment') || expl.toLowerCase().includes('blocking')) {
          sigElements.push(
            <text key="sig-dinic-block" x={W - 12} y={34}
              fill="#c084fc" fontSize="8" fontWeight="bold" textAnchor="end">blocking flow</text>,
          );
        }
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-flow-bottleneck"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#a78bfa" fontSize="8" fontWeight="bold" textAnchor="middle">bottleneck</text>,
            );
          }
        }
        break;

      case 'max-flow':
        // Generic max-flow
        if (expl.toLowerCase().includes('augment') || expl.toLowerCase().includes('path')) {
          sigElements.push(
            <text key="sig-flow" x={W - 12} y={20}
              fill="#3b82f6" fontSize="9" fontWeight="bold" textAnchor="end">augment</text>,
          );
        }
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-flow-bottleneck"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#60a5fa" fontSize="8" fontWeight="bold" textAnchor="middle">bottleneck</text>,
            );
          }
        }
        break;

      case 'min-cut':
        sigElements.push(
          <text key="sig-mincut" x={W - 12} y={20}
            fill="#ef4444" fontSize="9" fontWeight="bold" textAnchor="end">cut</text>,
        );
        break;

      case 'kosaraju':
        // Two-pass indicator
        const passMatch = expl.match(/pass\s*(\d)/i);
        const pass = passMatch ? passMatch[1] : '?';
        sigElements.push(
          <text key="sig-kosaraju" x={W - 12} y={20}
            fill="#c084fc" fontSize="9" fontWeight="bold" textAnchor="end">pass {pass}</text>,
        );
        break;

      case 'tarjan':
        // Low-link indicator
        if (cvPos) {
          sigElements.push(
            <text key="sig-tarjan" x={cvPos.x} y={cvPos.y + NODE_RADIUS + 24}
              fill="#c084fc" fontSize="8" fontWeight="bold" textAnchor="middle">lowlink</text>,
          );
        }
        break;

      case 'kahns':
      case 'topo-sort':
        // In-degree indicator
        sigElements.push(
          <text key="sig-topo" x={W - 12} y={20}
            fill="#fbbf24" fontSize="9" fontWeight="bold" textAnchor="end">in-deg=0</text>,
        );
        break;

      case 'dfs-topo':
        // Post-order push indicator
        if (cvPos) {
          sigElements.push(
            <text key="sig-dfs-topo" x={cvPos.x} y={cvPos.y - NODE_RADIUS - 28}
              fill="#fbbf24" fontSize="8" fontWeight="bold" textAnchor="middle">post-push</text>,
          );
        }
        break;

      case 'euler':
        // Euler tour edge marker
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-euler"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#22d3ee" fontSize="8" fontWeight="bold" textAnchor="middle">tour</text>,
            );
          }
        }
        break;

      case 'bridges':
        // Bridge detection indicator
        if (meta.currentEdge) {
          const fp = getPos(meta.currentEdge.from);
          const tp = getPos(meta.currentEdge.to);
          if (fp && tp) {
            sigElements.push(
              <text key="sig-bridges"
                x={(fp.x + tp.x) / 2} y={(fp.y + tp.y) / 2 - 12}
                fill="#f59e0b" fontSize="8" fontWeight="bold" textAnchor="middle">bridge?</text>,
            );
          }
        }
        break;

      case 'hopcroft-karp':
        // Matching indicator
        sigElements.push(
          <text key="sig-hopcroft" x={W - 12} y={20}
            fill="#ec4899" fontSize="9" fontWeight="bold" textAnchor="end">match</text>,
        );
        break;

      case 'min-cost-flow':
        sigElements.push(
          <text key="sig-mcf" x={W - 12} y={20}
            fill="#10b981" fontSize="9" fontWeight="bold" textAnchor="end">cost flow</text>,
        );
        break;

      case 'stoer-wagner':
        sigElements.push(
          <text key="sig-stoer" x={W - 12} y={20}
            fill="#ef4444" fontSize="9" fontWeight="bold" textAnchor="end">min-cut phase</text>,
        );
        break;

      case 'boykov':
        sigElements.push(
          <text key="sig-boykov" x={W - 12} y={20}
            fill="#06b6d4" fontSize="9" fontWeight="bold" textAnchor="end">s-t cut</text>,
        );
        break;

      case 'chu-liu':
        sigElements.push(
          <text key="sig-chuliu" x={W - 12} y={20}
            fill="#f97316" fontSize="9" fontWeight="bold" textAnchor="end">arb MST</text>,
        );
        break;

      case 'gomory-hu':
        sigElements.push(
          <text key="sig-gomory" x={W - 12} y={20}
            fill="#a78bfa" fontSize="9" fontWeight="bold" textAnchor="end">cut tree</text>,
        );
        break;

      case 'centroid':
        sigElements.push(
          <text key="sig-centroid" x={W - 12} y={20}
            fill="#22d3ee" fontSize="9" fontWeight="bold" textAnchor="end">centroid decomp</text>,
        );
        if (cvPos) sigElements.push(
          <circle key="sig-centroid-ring" cx={cvPos.x} cy={cvPos.y} r={28}
            fill="none" stroke="#22d3ee" strokeWidth={1.5} strokeDasharray="4,3" opacity={0.6} />,
        );
        break;

      case 'dominator':
        sigElements.push(
          <text key="sig-dom" x={W - 12} y={20}
            fill="#f472b6" fontSize="9" fontWeight="bold" textAnchor="end">dominator tree</text>,
        );
        if (cvPos) sigElements.push(
          <text key="sig-dom-label" x={cvPos.x} y={cvPos.y - 28}
            fill="#f472b6" fontSize="8" fontWeight="bold" textAnchor="middle">dom</text>,
        );
        break;

      case 'dsu-rollback':
        sigElements.push(
          <text key="sig-rollback" x={W - 12} y={20}
            fill="#f97316" fontSize="9" fontWeight="bold" textAnchor="end">DSU rollback</text>,
        );
        break;

      case 'hld':
        sigElements.push(
          <text key="sig-hld" x={W - 12} y={20}
            fill="#3b82f6" fontSize="9" fontWeight="bold" textAnchor="end">heavy-light</text>,
        );
        if (cvPos) sigElements.push(
          <rect key="sig-hld-rect" x={cvPos.x - 16} y={cvPos.y - 16} width={32} height={32}
            fill="none" stroke="#3b82f6" strokeWidth={1.5} rx={4} opacity={0.6} />,
        );
        break;

      case 'link-cut':
        sigElements.push(
          <text key="sig-lct" x={W - 12} y={20}
            fill="#a855f7" fontSize="9" fontWeight="bold" textAnchor="end">link-cut tree</text>,
        );
        break;

      case 'micali-vazirani':
        sigElements.push(
          <text key="sig-mv" x={W - 12} y={20}
            fill="#fbbf24" fontSize="9" fontWeight="bold" textAnchor="end">perfect matching</text>,
        );
        break;

      case 'rerooting':
        sigElements.push(
          <text key="sig-rr" x={W - 12} y={20}
            fill="#34d399" fontSize="9" fontWeight="bold" textAnchor="end">rerooting DP</text>,
        );
        if (cvPos) sigElements.push(
          <text key="sig-rr-label" x={cvPos.x} y={cvPos.y - 28}
            fill="#34d399" fontSize="8" fontWeight="bold" textAnchor="middle">re-root</text>,
        );
        break;

      case 'bfs-dp':
        sigElements.push(
          <text key="sig-bfsdp" x={W - 12} y={20}
            fill="#60a5fa" fontSize="9" fontWeight="bold" textAnchor="end">BFS+DP hybrid</text>,
        );
        break;

      case 'blossom':
        sigElements.push(
          <text key="sig-blossom" x={W - 12} y={20}
            fill="#ec4899" fontSize="9" fontWeight="bold" textAnchor="end">blossom matching</text>,
        );
        if (cvPos) sigElements.push(
          <polygon key="sig-blossom-poly" points={`${cvPos.x},${cvPos.y - 20} ${cvPos.x + 18},${cvPos.y + 12} ${cvPos.x - 18},${cvPos.y + 12}`}
            fill="none" stroke="#ec4899" strokeWidth={1.5} opacity={0.5} />,
        );
        break;

      case 'top-trees':
        sigElements.push(
          <text key="sig-toptree" x={W - 12} y={20}
            fill="#fbbf24" fontSize="9" fontWeight="bold" textAnchor="end">top tree</text>,
        );
        if (cvPos) sigElements.push(
          <rect key="sig-toptree-rect" x={cvPos.x - 14} y={cvPos.y - 14} width={28} height={28}
            fill="none" stroke="#fbbf24" strokeWidth={1.5} rx={3} opacity={0.6} />,
        );
        break;

      case 'virtual-tree':
        sigElements.push(
          <text key="sig-vtree" x={W - 12} y={20}
            fill="#34d399" fontSize="9" fontWeight="bold" textAnchor="end">virtual tree</text>,
        );
        if (cvPos) sigElements.push(
          <circle key="sig-vtree-ring" cx={cvPos.x} cy={cvPos.y} r={NODE_RADIUS + 8}
            fill="none" stroke="#34d399" strokeWidth={1} strokeDasharray="3 2" opacity={0.5} />,
        );
        break;

      case 'convex-hull':
        sigElements.push(
          <text key="sig-hull" x={W - 12} y={20}
            fill="#22d3ee" fontSize="9" fontWeight="bold" textAnchor="end">convex hull</text>,
        );
        if (cvPos) sigElements.push(
          <circle key="sig-hull-ring" cx={cvPos.x} cy={cvPos.y} r={NODE_RADIUS + 6}
            fill="none" stroke="#22d3ee" strokeWidth={1.5} opacity={0.5} />,
        );
        break;

      case 'closest-pair':
        sigElements.push(
          <text key="sig-cp" x={W - 12} y={20}
            fill="#f472b6" fontSize="9" fontWeight="bold" textAnchor="end">closest pair</text>,
        );
        if (cvPos) sigElements.push(
          <line key="sig-cp-line" x1={cvPos.x - 12} y1={cvPos.y} x2={cvPos.x + 12} y2={cvPos.y}
            stroke="#f472b6" strokeWidth={2} opacity={0.5} />,
        );
        break;

      case 'line-intersection':
        sigElements.push(
          <text key="sig-li" x={W - 12} y={20}
            fill="#fbbf24" fontSize="9" fontWeight="bold" textAnchor="end">line intersection</text>,
        );
        if (cvPos) sigElements.push(
          <line key="sig-li-x1" x1={cvPos.x - 10} y1={cvPos.y - 10} x2={cvPos.x + 10} y2={cvPos.y + 10}
            stroke="#fbbf24" strokeWidth={1.5} opacity={0.5} />,
          <line key="sig-li-x2" x1={cvPos.x - 10} y1={cvPos.y + 10} x2={cvPos.x + 10} y2={cvPos.y - 10}
            stroke="#fbbf24" strokeWidth={1.5} opacity={0.5} />,
        );
        break;

      case 'point-in-polygon':
        sigElements.push(
          <text key="sig-pip" x={W - 12} y={20}
            fill="#a78bfa" fontSize="9" fontWeight="bold" textAnchor="end">point in polygon</text>,
        );
        if (cvPos) sigElements.push(
          <circle key="sig-pip-dot" cx={cvPos.x} cy={cvPos.y} r={3}
            fill="#a78bfa" opacity={0.7} />,
        );
        break;

      case 'sat':
        sigElements.push(
          <text key="sig-sat" x={W - 12} y={20}
            fill="#f97316" fontSize="9" fontWeight="bold" textAnchor="end">2-SAT</text>,
        );
        if (cvPos) sigElements.push(
          <rect key="sig-sat-rect" x={cvPos.x - 12} y={cvPos.y - 12} width={24} height={24}
            fill="none" stroke="#f97316" strokeWidth={1} strokeDasharray="2 2" opacity={0.5} />,
        );
        break;

      default:
        break;
    }
  }

  // ── KD-tree spatial overlay ───────────────────────────────────────────────
  const kdOverlayElements: React.ReactElement[] = [];

  if (meta.kdAxis && meta.kdSplitValue !== undefined) {
    // Draw a dashed vertical (x-axis split) or horizontal (y-axis split) line
    const split = meta.kdSplitValue;
    if (meta.kdAxis === 'x') {
      kdOverlayElements.push(
        <line key="kd-split" x1={split} y1={0} x2={split} y2={H}
          stroke="#a855f7" strokeWidth={1.5} strokeDasharray="6,4" opacity={0.5} />,
      );
      kdOverlayElements.push(
        <text key="kd-split-label" x={split + 4} y={14}
          fill="#c4b5fd" fontSize="9" fontWeight="bold">
          x={split}
        </text>,
      );
    } else {
      kdOverlayElements.push(
        <line key="kd-split" x1={0} y1={split} x2={W} y2={split}
          stroke="#a855f7" strokeWidth={1.5} strokeDasharray="6,4" opacity={0.5} />,
      );
      kdOverlayElements.push(
        <text key="kd-split-label" x={4} y={split - 4}
          fill="#c4b5fd" fontSize="9" fontWeight="bold">
          y={split}
        </text>,
      );
    }
  }

  if (meta.kdQueryRect) {
    const r = meta.kdQueryRect;
    kdOverlayElements.push(
      <rect key="kd-query" x={r.x} y={r.y} width={r.w} height={r.h}
        fill="rgba(168,85,247,0.08)" stroke="#a855f7" strokeWidth={1.5}
        strokeDasharray="3,3" opacity={0.7} />,
    );
    kdOverlayElements.push(
      <text key="kd-query-label" x={r.x + 4} y={r.y + 12}
        fill="#c4b5fd" fontSize="8" fontWeight="bold">
        query range
      </text>,
    );
  }

  // ── HUD: queue / stack display ────────────────────────────────────────────

  const queueDisplay = meta.queue?.length
    ? `Queue: [${meta.queue.slice(0, 8).join(', ')}${meta.queue.length > 8 ? '…' : ''}]`
    : meta.stack?.length
    ? `Stack: [${meta.stack.slice(0, 8).join(', ')}${meta.stack.length > 8 ? '…' : ''}]`
    : meta.priorityQueue?.length
    ? `Open: [${meta.priorityQueue.slice(0, 8).join(', ')}${meta.priorityQueue.length > 8 ? '…' : ''}]`
    : null;

  // ── Render ────────────────────────────────────────────────────────────────

  const currentLabel = meta.currentVertex
    ? `Current vertex: ${meta.currentVertex}.`
    : '';
  const stepDescription = currentStep?.metadata?.explanation ?? '';

  // Truncate description for overlay (max ~70 chars)
  const overlayText = stepDescription.length > 70
    ? stepDescription.slice(0, 67) + '…'
    : stepDescription;
  const overlayW = Math.min(W - 16, 420);

  return (
    <div ref={containerRef} className="relative w-full h-full overflow-hidden">
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', background: 'transparent' }}
        role="img"
        aria-label={`Graph visualization. ${currentLabel} ${stepDescription}`.trim()}
      >
        <title>{`Graph visualization${meta.currentVertex ? ` — active: ${meta.currentVertex}` : ''}`}</title>
        {markerDefs}

        {/* Edges — bottom z layer */}
        <g>{edgeElements}</g>

        {/* Traversal tree overlay — above edges, below nodes */}
        {traversalTreeElements.length > 0 && <g>{traversalTreeElements}</g>}

        {/* Nodes — middle z layer */}
        <g>{nodeElements}</g>

        {/* Focal highlights — top z layer */}
        {highlightElements.length > 0 && <g>{highlightElements}</g>}

        {/* KD-tree spatial overlay */}
        {kdOverlayElements.length > 0 && <g>{kdOverlayElements}</g>}

        {/* Algorithm signature overlay */}
        {sigElements.length > 0 && <g>{sigElements}</g>}

        {/* HUD chip — queue / stack */}
        {queueDisplay && (
          <g>
            <rect
              x={8} y={H - 28}
              width={Math.min(W - 16, queueDisplay.length * 6.5 + 16)}
              height={20}
              rx={5}
              fill="rgba(15,23,42,0.88)"
              stroke="#1e3a5f"
              strokeWidth={0.5}
            />
            <text
              x={16}
              y={H - 18}
              fill="#60a5fa"
              fontSize="9"
              fontFamily="monospace"
              dominantBaseline="middle"
              className="select-none"
            >
              {queueDisplay}
            </text>
          </g>
        )}

        {/* Step explanation overlay — top-left */}
        {overlayText && (
          <g>
            <rect
              x={8} y={8}
              width={overlayW}
              height={22}
              rx={6}
              fill="rgba(10,10,14,0.82)"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth={0.5}
            />
            <text
              x={16}
              y={19}
              fill="#a1a1aa"
              fontSize="10"
              fontFamily="monospace"
              dominantBaseline="middle"
              className="select-none"
            >
              {overlayText}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
});

export default GraphVisualizer;
